import React, { useCallback, useState } from 'react';

import type { ChangeEventHandler } from 'react';
import type { UserData } from '../../../../../public-src/core/js/schibsted-account/types.js';
import type { NewsletterConfig } from './types.js';

import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { ToggleSwitch } from '../commonComponents/ToggleSwitch.js';
import { NEWSLETTERS_HOME_URL } from './consts.js';
import { newsletterByfunnApi as newsletterApi } from '../../../../../public-src/aftenposten/js/newsletter-api.js';
import handleLocalStorage from './handleLocalStorage.js';

interface LoggedInProps extends NewsletterConfig {
  newsletterId: string;
  session: UserData;
}

export const getAccess = (access: NewsletterConfig['access']) => {
  if (access === 'open') {
    return (
      <span className="newsletter-access">Nyhetsbrev | Ukentlig | Gratis</span>
    );
  }

  return <></>;
};

export const LoggedIn: React.FC<LoggedInProps> = ({
  newsletterId,
  title,
  access,
  description,
  session,
}) => {
  const [latestUserAction, setLatestUserAction] = useState<
    'subscribe' | 'unsubscribe' | null
  >(null);
  const [isError, setIsError] = useState<boolean>(false);
  const userId = session.userId;
  const userToken = session.userToken;
  const { trackClickUIElement } = usePulseContext();

  const LS = handleLocalStorage({
    newsletterId: newsletterId,
    newsletterPublication: 'aftenposten',
    userId,
  });

  const subcribeUser = useCallback(async () => {
    if (!userToken) return;

    await newsletterApi.subscribe(newsletterId);

    setLatestUserAction('subscribe');
    LS.setSubscribedData();
    LS.deleteNotSubscribedData();

    setIsError(false);
  }, [LS, newsletterId, userToken]);

  const unsubscribeUser = async () => {
    if (!userToken) return;

    await newsletterApi.unsubscribe(newsletterId);

    setLatestUserAction('unsubscribe');
    LS.deleteSubscribedData();
    LS.setNotSubscribedData();

    setIsError(false);
  };

  const handleToggleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if ((e.currentTarget as HTMLInputElement).checked) {
      trackClickUIElement({
        object: {
          id: `subscribe-newsletter-${newsletterId}`,
          name: `Subscription to newsletter ${newsletterId}`,
          elementType: 'Button',
        },
      }).finally(() => subcribeUser().catch(() => setIsError(true)));
    } else {
      trackClickUIElement({
        object: {
          id: `unsubscribe-newsletter-${newsletterId}`,
          name: `Cancellation of subscription to ${newsletterId}`,
          elementType: 'Button',
        },
      }).finally(() => unsubscribeUser().catch(() => setIsError(true)));
    }
  };

  const getFeedbackText = (): string => {
    if (latestUserAction === 'subscribe') {
      return 'Du er nå påmeldt Vinks ukentlige nyhetsbrev!';
    }
    if (latestUserAction === 'unsubscribe') {
      return 'Du er nå avmeldt nyhetsbrevet.';
    }
    if (isError) {
      return 'Noe gikk galt, vennligst prøv igjen senere.';
    }

    return '';
  };

  return (
    <div
      className={`newsletter-container ${
        isError ? 'newsletter-signup-error' : ''
      }`}
    >
      <div className="newsletter-title">{title}</div>
      <div className="newsletter-description-texts">
        {getFeedbackText() === '' ? (
          <p>{description}</p>
        ) : (
          <p className="newsletter-feedback">{getFeedbackText()}</p>
        )}
        {latestUserAction === 'subscribe' && (
          <a href={NEWSLETTERS_HOME_URL}>Se dine nyhetsbrev.</a>
        )}
      </div>
      <div className="newsletter-footer">
        {getAccess(access)}
        <ToggleSwitch
          ariaLabel={
            latestUserAction === 'subscribe'
              ? 'Meld av nyhetsbrev'
              : 'Meld på nyhetsbrev'
          }
          checked={latestUserAction === 'subscribe'}
          disabled={false}
          handleToggle={handleToggleChange}
        />
      </div>
    </div>
  );
};
